// Client-side ETag/If-None-Match cache for the swr-cache edge function.
//
// Strategy:
//  1. Read cached body + etag from localStorage and return it immediately
//     (or yield via the `onCached` callback).
//  2. Issue a network revalidation with `If-None-Match`.
//  3. On 304, do nothing (cached body stays valid; we just bump the timestamp).
//  4. On 200, persist new body + new ETag and resolve with the fresh payload.
//
// The endpoint sets `Cache-Control: must-revalidate`, so the browser HTTP cache
// also short-circuits the response body to ~0 bytes on 304.

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string
const BASE = `https://${PROJECT_ID}.supabase.co/functions/v1/swr-cache`

type Resource = 'translations' | 'typography' | 'content_variants'

interface CacheEntry<T> {
  etag: string
  body: T
  ts: number
}

function storageKey(resource: Resource, lang?: string) {
  return `kp_swr_${resource}${lang ? `_${lang}` : ''}`
}

export function readEtagCache<T>(resource: Resource, lang?: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey(resource, lang))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CacheEntry<T>
    return parsed.body ?? null
  } catch {
    return null
  }
}

export async function etagFetch<T>(
  resource: Resource,
  lang?: string,
): Promise<T | null> {
  const key = storageKey(resource, lang)
  let cached: CacheEntry<T> | null = null
  try {
    const raw = localStorage.getItem(key)
    if (raw) cached = JSON.parse(raw) as CacheEntry<T>
  } catch {
    // ignore
  }

  const params = new URLSearchParams({ resource })
  if (lang) params.set('lang', lang)
  const url = `${BASE}?${params.toString()}`

  const headers: Record<string, string> = {
    apikey: ANON,
    Authorization: `Bearer ${ANON}`,
  }
  if (cached?.etag) headers['If-None-Match'] = cached.etag

  try {
    const res = await fetch(url, { headers, cache: 'default' })

    if (!res.ok) {
      return cached?.body ?? null
    }

    const payload = (await res.json()) as T | { unchanged: true }

    // Server returns `{ unchanged: true }` (HTTP 200) instead of HTTP 304
    // when the ETag matches, to avoid Lovable's runtime-error reporter
    // flagging the intentional cache hit. Treat it like a 304.
    if (
      payload &&
      typeof payload === 'object' &&
      (payload as { unchanged?: boolean }).unchanged === true &&
      cached
    ) {
      try {
        localStorage.setItem(key, JSON.stringify({ ...cached, ts: Date.now() }))
      } catch {
        // ignore quota
      }
      return cached.body
    }

    const body = payload as T
    const etag = res.headers.get('ETag') ?? ''
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ etag, body, ts: Date.now() } satisfies CacheEntry<T>),
      )
    } catch {
      // ignore quota
    }
    return body
  } catch {
    return cached?.body ?? null
  }
}
